import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { PeriodQuery, ResolvedPeriod } from '@sl/core';

// WHAT THE SERVER ACTUALLY ANSWERED FOR THE RANGE WE ASKED FOR — `meta.period`, API §12.1 (D-588, D-589).
//
// `meta.period` is "the server's echo of the four dates", and the contract deliberately has no "I clamped
// this" flag: **a client that wants the served dates compares the echo with what it asked** (D-588). That is
// this file, and it exists because of one real case (D-589): a live dataset's own today is the pull's day, so
// a URL that carries a custom range running past it is answered with the range that exists — and a header
// showing the range nobody has numbers for is the bug BUG-141 went looking for.
//
// **It costs no read.** Every period-keyed answer already carries `meta.period` and every one of them is in
// the query cache under a key whose last element is `{ since, until, compare }` (`@sl/core/queries` `keys.ts`).
// So this watches the cache instead of asking a ninth question: the first screen's own read is the echo, and
// a period the app has not asked about yet simply has no echo, which is the right answer (`null` → show the
// ask). Nothing here derives a clamp; it only reports what came back.
//
// **It listens for ONE kind of event, and reads only the query that event is about.** The cache fires on every
// observer added, removed and re-rendered — dozens of events per interaction — and the first version of this
// file re-scanned the whole cache on each of them, from inside the notify batch. It worked and it was wrong:
// it made the whole app measurably slower (a 15 s timeout in the overlay sweep) and it turned two tests of
// other screens red by changing when their intermediate states were observable. A successful fetch is the only
// event that can carry a new echo, and the event names its own query, so both are O(1).
//
// **…and it subscribes only on a LIVE account** (D-626, T-306 — the phone found this and the web owed it). A
// clamp is only ever possible on a live dataset: the rule is keyed off the rebase (`RebasedDemo.live`, D-586)
// and the demo dataset is unchanged (D-585), so on a demo account the echo always equals the ask and this
// subscription has nothing to learn. What it still DOES there is schedule a `PeriodProvider` state update —
// a provider above every screen — from inside react-query's notify batch, which is the same trap D-611 names
// one paragraph up: it moves when the screens below observe their own intermediate states. On the phone, four
// `staged/quickAction.test.tsx` cases went red under fake timers because of exactly that. So the subscription
// exists exactly where the fact it reports can exist. It is a correctness line, not a saving.

/** The `{ since, until, … }` tail every period-keyed query key ends with. */
function periodTailOf(key: readonly unknown[]): { since?: unknown; until?: unknown } | null {
  const last = key[key.length - 1];
  return typeof last === 'object' && last !== null && !Array.isArray(last)
    ? (last as { since?: unknown; until?: unknown })
    : null;
}

function sameDates(a: ResolvedPeriod | null, b: ResolvedPeriod | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.since === b.since &&
    a.until === b.until &&
    a.compareSince === b.compareSince &&
    a.compareUntil === b.compareUntil
  );
}

/**
 * `meta.period` off any cached answer to THIS ask, or `null` while nothing has answered yet. The first match
 * wins: every endpoint resolves one ask the same way (one `resolveQuery`, API §1.5), so a second opinion
 * would be a server bug rather than a choice this function has to make.
 *
 * `enabled` IS "this is a live account" (D-626) — see the header note: off the live path there is no clamp to
 * report, and subscribing anyway only moves when other screens' states become observable.
 */
export function useServerPeriod(ask: PeriodQuery, enabled: boolean): ResolvedPeriod | null {
  const cache = useQueryClient().getQueryCache();
  const [echo, setEcho] = useState<ResolvedPeriod | null>(null);
  const { since, until } = ask;

  useEffect(() => {
    if (!enabled) return;
    const matches = (key: readonly unknown[]) => {
      const tail = periodTailOf(key);
      return tail !== null && tail.since === since && tail.until === until;
    };
    const take = (period: ResolvedPeriod | undefined) => {
      if (period) setEcho((was) => (sameDates(was, period) ? was : period));
    };

    // Once per ask: an answer already in the cache — a period the user has been to before, or a screen that
    // read before this provider mounted — is an echo too.
    for (const query of cache.getAll())
      if (matches(query.queryKey)) take(periodOf(query.state.data));

    return cache.subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'success') return;
      if (!matches(event.query.queryKey)) return;
      take(periodOf(event.query.state.data));
    });
  }, [cache, since, until, enabled]);

  return echo;
}

function periodOf(data: unknown): ResolvedPeriod | undefined {
  return (data as { meta?: { period?: ResolvedPeriod } } | undefined)?.meta?.period;
}
